import { FiRefreshCw } from 'react-icons/fi';

export const paraphrasingData = {
  courseId: 'paraphrasing',
  courseType: 'vocabulary',
  title: 'Paraphrasing Techniques',
  icon: FiRefreshCw,
  overview: {
    impact: 'Essential for high Writing scores',
    focus: 'Saying the same thing differently',
    uses: 'Writing Task 1 & 2, Reading',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Why Paraphrasing Matters',
      content: `<p>Paraphrasing is crucial for IELTS success:</p>`,
      points: [
        'Avoid copying question words (reduces Task Response score)',
        'Show vocabulary range (improves Lexical Resource)',
        'Understand Reading questions (find paraphrased answers)',
        'Demonstrate language flexibility',
      ],
    },
    {
      title: 'Paraphrasing Techniques',
      content: `<p>Use these strategies to paraphrase effectively:</p>`,
      table: {
        headers: ['Technique', 'Original', 'Paraphrased'],
        rows: [
          ['Synonyms', 'important', 'significant, crucial, essential'],
          ['Word form change', 'Technology benefits society', 'Society benefits from technology'],
          ['Active ↔ Passive', 'People use phones', 'Phones are used by people'],
          ['Different structure', 'Because of pollution...', 'Due to pollution... / Pollution causes...'],
          ['Clause to phrase', 'when people travel', 'during travel / while traveling'],
        ],
      },
    },
    {
      title: 'Common Synonym Groups',
      content: `<p>Memorize these useful synonym groups:</p>`,
      points: [
        '<strong>Important:</strong> significant, crucial, vital, essential, key',
        '<strong>Increase:</strong> rise, grow, surge, escalate, expand',
        '<strong>Decrease:</strong> decline, fall, drop, reduce, diminish',
        '<strong>Think:</strong> believe, consider, argue, maintain, assert',
        '<strong>Show:</strong> demonstrate, indicate, illustrate, reveal',
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Best Paraphrase',
      type: 'select',
      instructions: 'Which is the best paraphrase?',
      questions: [
        {
          text: 'Original: "Technology has changed our lives significantly."',
          options: [
            'Our lives have been transformed by technology.',
            'Technology has changed our lives significantly.',
            'Technology is good.',
          ],
          answer: 'Our lives have been transformed by technology.',
          explanation: 'Uses passive voice and synonym (transformed for changed).',
        },
        {
          text: 'Original: "Many people believe education is important."',
          options: [
            'Education is considered essential by numerous individuals.',
            'Many people believe education is important.',
            'People like education.',
          ],
          answer: 'Education is considered essential by numerous individuals.',
          explanation: 'Uses passive, synonyms (essential, numerous, individuals).',
        },
        {
          text: 'Original: "The chart shows a significant increase in sales."',
          options: [
            'A substantial rise in sales is illustrated in the chart.',
            'The chart shows a significant increase in sales.',
            'Sales went up.',
          ],
          answer: 'A substantial rise in sales is illustrated in the chart.',
          explanation: 'Uses synonyms (substantial, rise, illustrated) and passive voice.',
        },
        {
          text: 'Original: "People spend too much time on social media."',
          options: [
            'Excessive time is devoted to social networking platforms.',
            'People spend too much time on social media.',
            'Social media is bad.',
          ],
          answer: 'Excessive time is devoted to social networking platforms.',
          explanation: 'Uses passive voice and synonyms (excessive, devoted, platforms).',
        },
      ],
    },
    {
      title: 'Identify the Paraphrasing Technique',
      type: 'select',
      instructions: 'What technique was used to paraphrase?',
      questions: [
        {
          text: 'Original: "Technology benefits society."\nParaphrase: "Society benefits from technology."',
          options: ['Word order change', 'Synonym replacement', 'Active to passive'],
          answer: 'Word order change',
          explanation: 'The subject and object were swapped (technology → society as subject).',
        },
        {
          text: 'Original: "People use smartphones."\nParaphrase: "Smartphones are used by people."',
          options: ['Active to passive', 'Synonym replacement', 'Word order change'],
          answer: 'Active to passive',
          explanation: 'The sentence was changed from active to passive voice.',
        },
        {
          text: 'Original: "Important"\nParaphrase: "Significant"',
          options: ['Synonym replacement', 'Active to passive', 'Structure change'],
          answer: 'Synonym replacement',
          explanation: 'A word with similar meaning was used.',
        },
      ],
    },
    {
      title: 'Find the Synonym',
      type: 'fill-in',
      instructions: 'Type a synonym for each word.',
      questions: [
        {
          text: 'increase (verb)',
          answer: 'rise',
          explanation: 'Other synonyms: grow, surge, escalate, expand.',
        },
        {
          text: 'important (adjective)',
          answer: 'significant',
          explanation: 'Other synonyms: crucial, vital, essential, key.',
        },
        {
          text: 'show (verb)',
          answer: 'demonstrate',
          explanation: 'Other synonyms: indicate, illustrate, reveal, display.',
        },
        {
          text: 'problem (noun)',
          answer: 'issue',
          explanation: 'Other synonyms: challenge, difficulty, concern, obstacle.',
        },
      ],
    },
    {
      title: 'Paraphrase the IELTS Question',
      type: 'writing',
      instructions: 'Paraphrase these IELTS-style questions for your introduction.',
      questions: [
        {
          text: '"Some people think that governments should spend money on public services rather than wasting it on the arts."',
          example: 'There is a view that public funding should be directed towards essential services instead of being allocated to artistic endeavors.',
          tip: 'Replace: spend→directed/allocated, wasting→being allocated, arts→artistic endeavors.',
        },
        {
          text: '"The government should invest in public transportation."',
          example: 'Investment in public transport systems by the authorities is considered necessary by some.',
          tip: 'Change structure: verb→noun (invest→investment), add hedging.',
        },
        {
          text: '"Climate change is causing extreme weather events."',
          example: 'Extreme meteorological phenomena are increasingly being attributed to global warming.',
          tip: 'Use passive voice, replace: climate change→global warming, causing→attributed to.',
        },
      ],
    },
    {
      title: 'Spot the Copied Words',
      type: 'select',
      instructions: 'Which paraphrase copies too many words from the original?',
      questions: [
        {
          text: 'Original: "Technology has revolutionized communication."\nWhich paraphrase is POOR?',
          options: [
            'Technology has revolutionized the way people communicate.',
            'The communication revolution has been driven by technological advances.',
          ],
          answer: 'Technology has revolutionized the way people communicate.',
          explanation: 'This copies "technology has revolutionized"—too similar to the original.',
        },
        {
          text: 'Original: "Many countries face environmental problems."\nWhich paraphrase is POOR?',
          options: [
            'Many countries are facing environmental problems today.',
            'Environmental challenges are encountered by numerous nations.',
          ],
          answer: 'Many countries are facing environmental problems today.',
          explanation: 'This only adds "today"—most key words are unchanged.',
        },
      ],
    },
  ],
  tips: [
    'Never copy the exact words from the question',
    'Build a strong synonym vocabulary',
    'Practice changing sentence structures',
    'Use paraphrasing in your introduction immediately',
  ],
  dailyPractice: [
    'Paraphrase IELTS question prompts',
    'Build synonym lists for common words',
    'Practice changing active to passive',
    'Rewrite sentences using different structures',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Paraphrasing Techniques: Essential skill for IELTS including synonyms, structure changes, and practice exercises.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...paraphrasingData,
      title: paraphrasingData.title + ' (Foundations)',
      overview: {
        ...paraphrasingData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: paraphrasingData.sections.slice(0, Math.max(2, Math.ceil(paraphrasingData.sections.length / 2))),
      exercises: paraphrasingData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...paraphrasingData,
      title: paraphrasingData.title + ' (Advanced)',
      overview: {
        ...paraphrasingData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: paraphrasingData.sections,
      exercises: paraphrasingData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(paraphrasingData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return paraphrasingData;
}
